import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkEmailExists } from "../../../services/clientService";
import { DeliveryOption } from "../../../interfaces/deliveryOption";
import { getDeliveryOptions } from "../../../services/deliveryOptionsService";
import {
  getStates,
  isValidEmail,
  isValidPhone,
  isValidZip,
} from "../../../services/helpers";
import { useRegistrationContext } from "../../../contexts/ClientRegistrationContext";
import ToolTip from "../../../components/component-tools/ToolTip";

const KeyInformation = () => {
  const { data, updateData } = useRegistrationContext();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const states = getStates();

  useEffect(() => {
    const initializeData = async () => {
      try {
        const options = await getDeliveryOptions();
        setDeliveryOptions(options);
      } catch (error) {
        console.error("Error fetching delivery options:", error);
      }
    };

    initializeData();
  }, []);

  // Redirect to registration if data not there
  useEffect(() => {
    if (!data.userAgreement) {
      navigate("/registration");
    }
  }, [data.userAgreement, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    target: "client" | "additionalContact",
  ) => {
    const updatedField = { [field]: e.target.value };

    if (target === "client") {
      updateData({ client: { ...data.client!, ...updatedField } });
    } else if (target === "additionalContact") {
      updateData({
        additionalContact: { ...data.additionalContact!, ...updatedField },
      });
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    if (!data.client?.clientName)
      errors.clientName = "Organization Name is required.";

    if (!data.client?.phoneNumber)
      errors.phoneNumber = "Phone Number is required.";
    else if (!isValidPhone(data.client.phoneNumber))
      errors.phoneNumber = "Please enter a valid phone number.";

    if (data.client?.fax && !isValidPhone(data.client.fax)) {
      errors.fax = "Please enter a valid fax number.";
    }

    if (!data.client?.address) errors.address = "Address is required.";
    if (!data.client?.city) errors.city = "City is required.";
    if (!data.client?.state) errors.state = "State is required.";

    if (!data.client?.zipCode) errors.zipCode = "Zip Code is required.";
    else if (!isValidZip(data.client.zipCode))
      errors.zipCode = "Please enter a valid zip code.";

    if (!data.client?.primaryFirstName)
      errors.primaryFirstName = "First Name is required.";
    if (!data.client?.primaryLastName)
      errors.primaryLastName = "Last Name is required.";
    if (!data.client?.primaryEmail)
      errors.primaryEmail = "Primary Email is required.";
    else if (!isValidEmail(data.client.primaryEmail))
      errors.primaryEmail = "Please enter a valid email address.";

    if (
      data.client?.primaryEmail &&
      (await checkEmailExists(data.client.primaryEmail, data.client.clientID))
    ) {
      errors.primaryEmail = "Email already in use.";
    }

    if (data.client?.primaryEmail === data.additionalContact?.contactEmail) {
      errors.contactEmail =
        "Primary Email and Contact Email cannot be the same.";
      errors.primaryEmail =
        "Primary Email and Contact Email cannot be the same.";
    }

    if (
      data.additionalContact?.contactEmail &&
      !isValidEmail(data.additionalContact.contactEmail)
    ) {
      errors.contactEmail = "Please enter a valid email address.";
    }
    if (
      data.additionalContact?.contactEmail &&
      (await checkEmailExists(
        data.additionalContact.contactEmail,
        data.client?.clientID ?? 0,
      ))
    ) {
      errors.contactEmail = "Email already in use.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNavigateToChooseDefaultPosCodes = async () => {
    if (await validateFields()) {
      window.scrollTo(0, 0);
      navigate("/client/registration/choose-default-position-codes");
    } else {
      const firstErrorElement = document.querySelector(".invalid-feedback");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Step 2: Provide Key Information</h1>
      </div>
      <div className="divider"></div>
      {/* User Agreement Client Form */}
      <div className="data-container">
        <div className="text-header" style={{ flex: "0 0 100%" }}>
          Profile Information
        </div>

        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Organization Name:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.clientName ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.clientName || ""}
              onChange={(e) => handleInputChange(e, "clientName", "client")}
            />
            {validationErrors.clientName && (
              <div className="invalid-feedback">
                {validationErrors.clientName}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Phone Number:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.phoneNumber ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.phoneNumber || ""}
              onChange={(e) => handleInputChange(e, "phoneNumber", "client")}
            />
            {validationErrors.phoneNumber && (
              <div className="invalid-feedback">
                {validationErrors.phoneNumber}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Confidential Fax:</label>
            <ToolTip
              tooltipText="We will not call before faxing, reports are faxed upon completion without notification"
              placement="top"
              className="info-icon-spec"
            />
          </div>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.fax ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.fax || ""}
              onChange={(e) => handleInputChange(e, "fax", "client")}
            />
            {validationErrors.fax && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.fax}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Address:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.address ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.address || ""}
              onChange={(e) => handleInputChange(e, "address", "client")}
            />
            {validationErrors.address && (
              <div className="invalid-feedback">{validationErrors.address}</div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">Address 2:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={data.client?.address2 || ""}
              onChange={(e) => handleInputChange(e, "address2", "client")}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">City:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.city ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.city || ""}
              onChange={(e) => handleInputChange(e, "city", "client")}
            />
            {validationErrors.city && (
              <div className="invalid-feedback">{validationErrors.city}</div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">State:</label>
          <div className="field-input">
            <select
              className={`form-select ${
                validationErrors.state ? "is-invalid" : ""
              }`}
              value={data.client?.state || ""}
              onChange={(e) => handleInputChange(e, "state", "client")}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.abbreviation} value={state.abbreviation}>
                  {state.name} ({state.abbreviation})
                </option>
              ))}
            </select>
            {validationErrors.state && (
              <div className="invalid-feedback">{validationErrors.state}</div>
            )}
          </div>
        </div>
        {data.client?.state === "INT" && (
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Country:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={data.client?.country || ""}
                onChange={(e) => handleInputChange(e, "country", "client")}
              />
            </div>
          </div>
        )}
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">ZipCode:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.zipCode ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.zipCode || ""}
              onChange={(e) => handleInputChange(e, "zipCode", "client")}
            />
            {validationErrors.zipCode && (
              <div className="invalid-feedback">{validationErrors.zipCode}</div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">Final Report Delivery:</label>
          <div
            style={{
              flex: "0 0 30%",
              alignSelf: "end",
              display: "flex",
              flexDirection: "row",
              gap: "5px",
            }}
          >
            <select
              className={`form-select ${
                validationErrors.deliveryOptionID ? "is-invalid" : ""
              }`}
              value={data.client?.deliveryOptionID || ""}
              onChange={(e) =>
                handleInputChange(e, "deliveryOptionID", "client")
              }
            >
              {deliveryOptions.map((option) => (
                <option
                  key={option.deliveryOptionID}
                  value={option.deliveryOptionID}
                >
                  {option.description}
                </option>
              ))}
            </select>
            <ToolTip
              tooltipText="We will not call before faxing, reports are faxed upon completion without notification"
              placement="top"
              className="info-icon-spec"
            />
            {validationErrors.deliveryOptionID && (
              <div className="invalid-feedback">
                {validationErrors.deliveryOptionID}
              </div>
            )}
          </div>
        </div>
        <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
          Primary Login/Contact Information *
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Title:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.primaryTitle ? "is-invalid" : ""
              }`}
              type="text"
              placeholder="Mr./Ms., The Rev., ..."
              value={data.client?.primaryTitle || ""}
              onChange={(e) => handleInputChange(e, "primaryTitle", "client")}
            />
            {validationErrors.primaryTitle && (
              <div className="invalid-feedback">
                {validationErrors.primaryTitle}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">First Name:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.primaryFirstName ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.primaryFirstName || ""}
              onChange={(e) =>
                handleInputChange(e, "primaryFirstName", "client")
              }
            />
            {validationErrors.primaryFirstName && (
              <div className="invalid-feedback">
                {validationErrors.primaryFirstName}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Last Name:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.primaryLastName ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.primaryLastName || ""}
              onChange={(e) =>
                handleInputChange(e, "primaryLastName", "client")
              }
            />
            {validationErrors.primaryLastName && (
              <div className="invalid-feedback">
                {validationErrors.primaryLastName}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Job Title:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.primaryJobTitle ? "is-invalid" : ""
              }`}
              type="text"
              value={data.client?.primaryJobTitle || ""}
              onChange={(e) =>
                handleInputChange(e, "primaryJobTitle", "client")
              }
            />
            {validationErrors.primaryJobTitle && (
              <div className="invalid-feedback">
                {validationErrors.primaryJobTitle}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Email:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.primaryEmail ? "is-invalid" : ""
              }`}
              type="email"
              value={data.client?.primaryEmail || ""}
              onChange={(e) => handleInputChange(e, "primaryEmail", "client")}
            />
            {validationErrors.primaryEmail && (
              <div className="invalid-feedback">
                {validationErrors.primaryEmail}
              </div>
            )}
          </div>
        </div>
        <div className="error">
          *All communications from Oxford Document Management will be sent to
          the attention of the primary contact
        </div>
        <div
          className="text-sub-header"
          style={{ flex: "0 0 100%", display: "flex", gap: "10px" }}
        >
          <div>Additional Login/Contact Information</div>
          <ToolTip
            tooltipText="This is optional and can be added later on."
            placement="right"
            className="info-icon-spec"
          ></ToolTip>
        </div>

        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Title:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              placeholder="Mr./Ms., The Rev., ..."
              value={data.additionalContact?.contactTitle || ""}
              onChange={(e) =>
                handleInputChange(e, "contactTitle", "additionalContact")
              }
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">First Name:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.contactFirstName ? "is-invalid" : ""
              }`}
              type="text"
              value={data.additionalContact?.contactFirstName || ""}
              onChange={(e) =>
                handleInputChange(e, "contactFirstName", "additionalContact")
              }
            />
            {validationErrors.contactFirstName && (
              <div className="invalid-feedback">
                {validationErrors.contactFirstName}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Last Name:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.contactLastName ? "is-invalid" : ""
              }`}
              type="text"
              value={data.additionalContact?.contactLastName || ""}
              onChange={(e) =>
                handleInputChange(e, "contactLastName", "additionalContact")
              }
            />
            {validationErrors.contactLastName && (
              <div className="invalid-feedback">
                {validationErrors.contactLastName}
              </div>
            )}
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Job Title:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={data.additionalContact?.contactJobTitle || ""}
              onChange={(e) =>
                handleInputChange(e, "contactJobTitle", "additionalContact")
              }
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Email:</label>
          <div className="field-input">
            <input
              className={`form-control ${
                validationErrors.contactEmail ? "is-invalid" : ""
              }`}
              type="email"
              value={data.additionalContact?.contactEmail || ""}
              onChange={(e) =>
                handleInputChange(e, "contactEmail", "additionalContact")
              }
            />
            {validationErrors.contactEmail && (
              <div className="invalid-feedback">
                {validationErrors.contactEmail}
              </div>
            )}
          </div>
        </div>
        <div className="text-body">
          Clients are invoiced on or about the 1st of each month for services
          commenced the prior month.
        </div>
      </div>
      <br></br>
      <div className="btn-center">
        <button
          className="oxbtn btn-green"
          onClick={handleNavigateToChooseDefaultPosCodes}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default KeyInformation;
